import { Component, Input, Output, EventEmitter } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  query?: {
    raw_query: string;
    extracted_at: string;
  };
  result?: {
    success: boolean;
    row_count: number;
    data: any[];
    note?: string;
    error?: string;
  };
  isLoading?: boolean;
  suggested_queries?: string[];
}

@Component({
  selector: 'app-chat-message',
  templateUrl: './chat-message.component.html',
  styleUrls: ['./chat-message.component.scss']
})
export class ChatMessageComponent {

  @Input() message: ChatMessage;
  @Output() querySuggestionClicked = new EventEmitter<string>();

  constructor(private sanitizer: DomSanitizer) { }

  /**
   * Handle suggested query click
   */
  onSuggestedQueryClick(query: string): void {
    this.querySuggestionClicked.emit(query);
  }

  /**
   * Get sender type based on role
   */
  getSender(): 'user' | 'ai' {
    return this.message.role === 'user' ? 'user' : 'ai';
  }

  /**
   * Format timestamp to readable time
   */
  formatTime(dateString: string): string {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return 'Invalid date';
    }
  }

  /**
   * Format message content with support for newlines, bullet points, and basic HTML
   */
  formatMessageContent(content: string): SafeHtml {
    if (!content) return this.sanitizer.sanitize(1, '') || '';

    // Escape HTML to prevent XSS
    let formatted = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    // Convert newlines to <br> tags
    formatted = formatted.replace(/\n/g, '<br>');

    // Convert bullet points (- text) to proper list items
    formatted = formatted.replace(/^- (.*?)(<br>|$)/gm, '<li>$1</li>');

    // Wrap consecutive <li> items in <ul>
    formatted = formatted.replace(/(<li>.*?<\/li>)/s, (match) => {
      return '<ul style="margin: 0.5rem 0; padding-left: 1.5rem;">' + match + '</ul>';
    });

    // Remove multiple consecutive <ul> tags and merge them
    formatted = formatted.replace(/<\/ul>\s*<ul[^>]*>/g, '');

    // Bold text wrapped in **text** or __text__
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/__(.*?)__/g, '<strong>$1</strong>');

    // Italic text wrapped in *text* or _text_
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
    formatted = formatted.replace(/_(.*?)_/g, '<em>$1</em>');

    // Convert currency amounts to highlighted format
    formatted = formatted.replace(/\$([0-9,]+(?:\.[0-9]{2})?)/g, '<span class="currency">$$$1</span>');

    // Add line breaks between paragraphs (double newline to <p> tags)
    // Already handled by <br> conversion above

    return this.sanitizer.bypassSecurityTrustHtml(formatted);
  }

}
